import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PackageOpen } from 'lucide-react'
import {
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { getStatusBadgeVariant } from '../../utils'
import { paths } from '@/utils/path'

interface LinkedComponentsTableProps {
    components: any[]
    onReturn: (ids: string[], label: string) => void
}

// One row per distinct component name inside a category. Individual physical
// components are shown as the expanded (nested) rows of each name-group.
interface NameGroupRow {
    key: string
    category: string
    name: string
    quantity: number
    items: any[]
}

// Visible inline "return to warehouse" button (not hidden in a ⋯ dropdown).
const ReturnButton = ({
    label,
    onClick,
}: {
    label: string
    onClick: () => void
}) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation()
            onClick()
        }}
        className="inline-flex items-center gap-1 rounded border border-blue-200 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
    >
        <PackageOpen className="h-3.5 w-3.5" />
        {label}
    </button>
)

const LinkedComponentsTable = ({
    components,
    onReturn,
}: LinkedComponentsTableProps) => {
    // Group by category → name, then flatten to name-group rows sorted by
    // category so same-category rows stay adjacent (category is a column).
    const rows = useMemo<NameGroupRow[]>(() => {
        const map = new Map<string, NameGroupRow>()
        components.forEach((c) => {
            const category = c.category || 'uncategorized'
            const name = c.name || 'unnamed'
            const key = `${category}::${name}`
            const group = map.get(key)
            if (group) group.items.push(c)
            else map.set(key, { key, category, name, quantity: 0, items: [c] })
        })
        return Array.from(map.values())
            .map((g) => ({ ...g, quantity: g.items.length }))
            .sort(
                (a, b) =>
                    a.category.localeCompare(b.category) ||
                    a.name.localeCompare(b.name)
            )
    }, [components])

    // Name-group columns (parent rows) — trailing column holds the group action.
    const columns = createColumns<NameGroupRow>([
        {
            key: 'category',
            title: 'Category',
            width: 160,
            render: (v) => <span className="font-semibold uppercase">{v}</span>,
        },
        {
            key: 'name',
            title: 'Component Name',
            width: 240,
            render: (v) => <span className="font-medium">{v}</span>,
        },
        { key: 'quantity', title: 'Quantity', width: 90 },
        {
            key: 'group_action',
            title: 'Thao tác',
            width: 170,
            headerClassName: 'text-right',
            cellClassName: 'text-right',
            render: (_v, row) => (
                <ReturnButton
                    label="Đưa Tất Cả Về Kho"
                    onClick={() =>
                        onReturn(
                            row.items.map((c) => c.id),
                            row.name
                        )
                    }
                />
            ),
        },
    ])

    // Individual-component columns rendered inside the expanded row — trailing
    // column holds the per-component action.
    const expandedColumns = createColumns<any>([
        {
            key: 'name',
            title: 'Component',
            width: 240,
            render: (v, row) => (
                <Link
                    to={`${paths.warehouseComponents}/${row.id}`}
                    className="text-blue-600 hover:underline"
                >
                    {v}
                </Link>
            ),
        },
        { key: 'ref', title: 'Ref', width: 160, render: (v) => v || '-' },
        {
            key: 'condition',
            title: 'Condition',
            width: 120,
            render: (v) => (
                <span className="text-muted-foreground">{v || '-'}</span>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            width: 120,
            render: (_v, row) => (
                <Badge variant={getStatusBadgeVariant(row.status)}>
                    {row.status?.toUpperCase() || 'AVAILABLE'}
                </Badge>
            ),
        },
        {
            key: 'item_action',
            title: 'Thao tác',
            width: 150,
            headerClassName: 'text-right',
            cellClassName: 'text-right',
            render: (_v, row) => (
                <ReturnButton
                    label="Đưa Về Kho"
                    onClick={() => onReturn([row.id], row.name)}
                />
            ),
        },
    ])

    return (
        <DataTableCustomized
            columns={columns}
            rows={rows}
            rowKey={(row) => row.key}
            size="small"
            expandable={{
                expandedColumns,
                expandedRowData: (row) => row.items,
                expandedRowKey: (item) => item.id,
                rowExpandable: (row) => row.items.length > 0,
            }}
        />
    )
}

export default LinkedComponentsTable
